import { getCurrencySymbol } from '@/constants';
import { CompositionType } from '@/enums';
import { IProductPriceRange } from '@/interfaces';
import { orderBy } from 'lodash';

export const PRODUCT_PHOTO_COLOR = {
  BUNDLE: '#00AEC7',
  KIT: '#C820FF',
  PROBIOTIC: '#2B36BA',
  // COACH_PROGRAM: '#FFAC00',
  FMT: '#FFAC00',
};

export const PRODUCT_PHOTO_ICON = {
  BUNDLE: 'box',
  KIT: 'laboratory',
  PROBIOTIC: 'supplement',
  // COACH_PROGRAM: 'starFilled',
  FMT: 'extraction',
};

// Badge colors for the Composition type column - same CSS-var palette as
// ORDER_STATUS_COLOR. success/error stay reserved for the Active badge.
export const COMPOSITION_TYPE_COLOR = {
  [CompositionType.RAW]: 'var(--warning)',
  [CompositionType.COMPONENT]: 'var(--link)',
  [CompositionType.PRODUCT]: 'var(--primary)',
};

// Shared by CreateProductWizard's ConfirmCreateProductModal (form values,
// string amounts) and ProductManagement's Currencies column (server
// `currencyPricings`, numeric amounts) — same range derivation as
// TieredPricingStep's live "to" display: base price from qty 1 up to (not
// including) the first tier's minQuantity, each tier up to the next tier's
// minQuantity, and the last tier as an open "≥" range. Returned as
// {label, value} pairs (rather than one joined string) so callers can render
// the range labels in a fixed-width column and have them line up — a
// single-tier entry still gets a "≥1" label instead of a bare price, for the
// same reason.
export type PricingRangeEntryLike = {
  currency?: string | null;
  price?: string | number | null;
  tieredPrices?:
    | ({
        minQuantity?: string | number | null;
        price?: string | number | null;
      } | null)[]
    | null;
};

export const buildPricingRanges = (entry: PricingRangeEntryLike) => {
  const symbol = getCurrencySymbol(entry.currency);
  const allTiers = (entry.tieredPrices ?? [])
    .filter((tier): tier is NonNullable<typeof tier> => !!tier)
    .sort((a, b) => Number(a.minQuantity) - Number(b.minQuantity));

  // Two shapes reach this function: the wizard/edit FORM's (base price is
  // its own `entry.price` field, `tieredPrices` holds only the tiers ABOVE
  // it) and the API's real one (no separate base price field any more -
  // it's folded into `tieredPrices` as its own `minQuantity: 1` entry, see
  // buildPricingPayload). Detect the latter by that leading minQuantity 1
  // tier - the form never produces one (its own tiers always start at 2+) -
  // and treat it as the base price instead of double-counting/missing it.
  const foldedBaseTier =
    Number(allTiers[0]?.minQuantity) === 1 ? allTiers[0] : undefined;
  const basePrice = foldedBaseTier ? foldedBaseTier.price : entry.price;
  const tiers = foldedBaseTier ? allTiers.slice(1) : allTiers;

  if (tiers.length === 0) {
    return [{ label: '≥1', value: `${symbol}${basePrice}` }];
  }

  const ranges = [
    {
      label: `1-${Number(tiers[0].minQuantity) - 1}`,
      value: `${symbol}${basePrice}`,
    },
  ];
  tiers.forEach((tier, index) => {
    const nextTier = tiers[index + 1];
    ranges.push({
      label: nextTier
        ? `${tier.minQuantity}-${Number(nextTier.minQuantity) - 1}`
        : `≥${tier.minQuantity}`,
      value: `${symbol}${tier.price}`,
    });
  });
  return ranges;
};

export const checkOverlap = (priceRangeInput: IProductPriceRange[]) => {
  const sorted = orderBy([...priceRangeInput], ['quantityFrom'], ['asc']);

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    // Check degenerate range and overlap
    if (next?.quantityFrom !== (current?.quantityTo ?? 0) + 1) return true;
  }

  return false;
};
